import React from 'react';
import clsx from 'clsx';
import {Swiper, SwiperSlide} from 'swiper/react';
import {Autoplay} from 'swiper';

//components
import Product from 'components/product/Product';
import {Link} from 'components/Link';

//types
import type {Product as ProductType} from '@shopify/hydrogen/storefront-api-types';

//styles
import 'swiper/swiper.min.css';

interface Props {
  title: string;
  products: (ProductType | null | undefined)[];
  viewAllTo?: string;
  viewAllLabel?: string;
  className?: string;
  /** Show sold-out cards as 即將到貨 / Coming Soon (e.g. New Arrivals). */
  comingSoon?: boolean;
}

/**
 * Continuous horizontal product marquee: Swiper autoplay with delay 0
 * plus a linear timing function (see .products-marquee in styles/app.css)
 * scrolls the cards steadily, pausing on hover so 加入購物車 stays
 * clickable. Swiper's loop needs more slides than are visible at once, so a
 * short selection is duplicated — while the row is moving, the repetition
 * just makes the cycle seamless.
 *
 * A row that cannot move is a different matter: with no more products than
 * the widest breakpoint shows, the duplicate lands on screen NEXT TO its
 * original and Swiper declines to loop so few slides, so nothing scrolls it
 * out of sight. One pre-order product became two identical cards side by
 * side on the homepage. Below that threshold the section is just the
 * products, standing still.
 */
export default function ProductsMarquee({
  title,
  products,
  viewAllTo,
  viewAllLabel,
  className,
  comingSoon,
}: Props) {
  const cards = products.filter(Boolean) as ProductType[];
  if (cards.length === 0) return <></>;
  // The widest breakpoint below shows five at once.
  const scrolls = cards.length > 5;
  const slides = scrolls && cards.length < 10 ? [...cards, ...cards] : cards;

  return (
    <section className={clsx('py-14', className)}>
      <header className="mb-8 text-center">
        <h2 className="text-xl md:text-section font-bold uppercase tracking-wide">
          {title}
        </h2>
        {viewAllTo && (
          <Link
            to={viewAllTo}
            prefetch="intent"
            className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-hk-orange transition-opacity duration-200 hover:opacity-75"
          >
            {viewAllLabel ?? '查看全部'}
            <span aria-hidden>→</span>
          </Link>
        )}
      </header>
      {/* Full-bleed: no Container padding, so the continuously-scrolling
          cards run edge-to-edge and pass BEHIND the fixed contact-CTA stack
          (matching the hero banner) instead of clipping at the container
          edge and leaving a white gutter under the CTA column. */}
      <div className="w-full">
        <Swiper
          className="products-marquee"
          modules={[Autoplay]}
          autoplay={
            scrolls && {
              delay: 0,
              disableOnInteraction: false,
              pauseOnMouseEnter: true,
            }
          }
          speed={5000}
          loop={scrolls}
          spaceBetween={20}
          slidesPerView={2}
          breakpoints={{
            768: {slidesPerView: 3},
            1280: {slidesPerView: 5},
          }}
        >
          {slides.map((p, i) => (
            // h-auto lets the slide stretch to the tallest card in the row
            // (see .products-marquee .swiper-slide in styles/app.css); the
            // h-full <ul> passes that height to the card so every card's
            // price + button line up regardless of title length or swatches.
            <SwiperSlide key={`marquee-${p.id}-${i}`} className="!h-auto">
              <ul className="grid h-full grid-cols-1">
                <Product product={p} quickAdd comingSoon={comingSoon} />
              </ul>
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
    </section>
  );
}
